import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoneyOff as UnstakeIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../api';

// Helper function to convert Wei to readable format
function weiToMoani(weiString) {
  if (!weiString) return 0;
  return parseFloat(weiString) / Math.pow(10, 18);
}

// Helper function to format staking period
function formatStakingPeriod(period) {
  const periodMap = {
    'ONE_MONTH': '1 Month',
    'TWO_MONTHS': '2 Months', 
    'THREE_MONTHS': '3 Months',
    'SIX_MONTHS': '6 Months'
  };
  return periodMap[period] || period;
}

function UserStakes({ userId }) {
  const queryClient = useQueryClient();
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unstakeDialogOpen, setUnstakeDialogOpen] = useState(false);
  const [selectedStake, setSelectedStake] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    amount: '',
    stakingPeriod: '',
    apyAtStaking: '',
    hasCommunityBoost: false,
    startTime: '',
    endTime: ''
  });
  
  // Unstake form state
  const [unstakeReason, setUnstakeReason] = useState('');

  // Fetch user data
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId
  });

  // Fetch user's stakes
  const { data: stakesData, isLoading, isError } = useQuery({
    queryKey: ['stakes', 'user', userId],
    queryFn: async () => {
      if (!userId) return { stakes: [], totalCount: 0 };
      const response = await api.get(`/stakes/user/${userId}`);
      return response.data;
    },
    enabled: !!userId
  });

  // Update stake mutation
  const updateStakeMutation = useMutation({
    mutationFn: async ({ stakeId, data }) => {
      const response = await api.put(`/stakes/${stakeId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stakes', 'user', userId]);
      setEditDialogOpen(false);
      setSelectedStake(null);
    }
  });

  // Delete stake mutation
  const deleteStakeMutation = useMutation({
    mutationFn: async (stakeId) => {
      const response = await api.delete(`/stakes/${stakeId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stakes', 'user', userId]);
      setDeleteDialogOpen(false);
      setSelectedStake(null);
    }
  });

  // Unstake mutation
  const unstakeMutation = useMutation({
    mutationFn: async ({ stakeId, reason }) => {
      const response = await api.post(`/stakes/${stakeId}/unstake`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stakes', 'user', userId]);
      queryClient.invalidateQueries(['user', userId]); // Refresh user balance
      setUnstakeDialogOpen(false);
      setSelectedStake(null);
      setUnstakeReason('');
    }
  });

  // Dialog handlers
  const handleEditStake = (stake) => {
    setSelectedStake(stake);
    setEditForm({
      amount: stake.amount.toString(),
      stakingPeriod: stake.stakingPeriod,
      apyAtStaking: stake.apyAtStaking.toString(),
      hasCommunityBoost: stake.hasCommunityBoost,
      startTime: stake.startTime ? new Date(stake.startTime).toISOString().slice(0, 16) : '',
      endTime: stake.endTime ? new Date(stake.endTime).toISOString().slice(0, 16) : ''
    });
    setEditDialogOpen(true);
  };

  const handleDeleteStake = (stake) => {
    setSelectedStake(stake);
    setDeleteDialogOpen(true);
  };

  const handleUnstakeStake = (stake) => {
    setSelectedStake(stake);
    setUnstakeDialogOpen(true);
  };

  const handleEditSubmit = () => {
    if (!selectedStake) return;
    
    updateStakeMutation.mutate({
      stakeId: selectedStake.id,
      data: {
        amount: parseFloat(editForm.amount),
        stakingPeriod: editForm.stakingPeriod,
        apyAtStaking: parseFloat(editForm.apyAtStaking),
        hasCommunityBoost: editForm.hasCommunityBoost,
        startTime: editForm.startTime || null,
        endTime: editForm.endTime || null
      }
    });
  };

  const handleDeleteConfirm = () => {
    if (!selectedStake) return;
    deleteStakeMutation.mutate(selectedStake.id);
  };

  const handleUnstakeConfirm = () => {
    if (!selectedStake) return;
    unstakeMutation.mutate({
      stakeId: selectedStake.id,
      reason: unstakeReason
    });
  };

  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">No user ID provided</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load user stakes.
      </Alert>
    );
  }

  const stakes = stakesData?.stakes || [];
  const activeStakes = stakes.filter(stake => stake.isActive);
  const completedStakes = stakes.filter(stake => !stake.isActive);
  const totalStaked = activeStakes.reduce((sum, stake) => sum + stake.amount, 0);
  const totalRewards = stakes.reduce((sum, stake) => sum + stake.rewardsClaimed, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUpIcon sx={{ mr: 2, color: 'primary.main' }} />
        Stakes for {userData?.name || 'User'}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="primary">
                {totalStaked.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Staked (MOANI)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="success.main">
                {activeStakes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Stakes
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CancelIcon sx={{ color: 'text.secondary', fontSize: 40, mb: 1 }} />
              <Typography variant="h6">
                {completedStakes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed Stakes
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScheduleIcon sx={{ color: 'warning.main', fontSize: 40, mb: 1 }} />
              <Typography variant="h6" color="warning.main">
                {totalRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Rewards (MOANI)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stakes Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Stake Details ({stakes.length} total)
        </Typography>
        
        {stakes.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No stakes found for this user.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Amount (MOANI)</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>APY</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rewards Claimed</TableCell>
                <TableCell>Community Boost</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stakes.map((stake) => (
                <TableRow key={stake.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stake.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={formatStakingPeriod(stake.stakingPeriod)} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {stake.apyAtStaking.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(stake.startTime).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(stake.endTime).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={stake.isActive ? 'Active' : 'Completed'} 
                      color={stake.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {stake.rewardsClaimed.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={stake.hasCommunityBoost ? 'Yes' : 'No'} 
                      color={stake.hasCommunityBoost ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditStake(stake)}
                        disabled={!stake.isActive}
                        title="Edit Stake"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUnstakeStake(stake)}
                        disabled={!stake.isActive}
                        color="warning"
                        title="Unstake (Return Principal)"
                      >
                        <UnstakeIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteStake(stake)}
                        color="error"
                        title="Delete Stake"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Edit Stake Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Stake</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Amount (MOANI)"
              type="number"
              value={editForm.amount}
              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ step: "0.01", min: "0" }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Staking Period</InputLabel>
              <Select
                value={editForm.stakingPeriod}
                onChange={(e) => setEditForm({ ...editForm, stakingPeriod: e.target.value })}
                label="Staking Period"
              >
                <MenuItem value="ONE_MONTH">1 Month</MenuItem>
                <MenuItem value="TWO_MONTHS">2 Months</MenuItem>
                <MenuItem value="THREE_MONTHS">3 Months</MenuItem>
                <MenuItem value="SIX_MONTHS">6 Months</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="APY (%)"
              type="number"
              value={editForm.apyAtStaking}
              onChange={(e) => setEditForm({ ...editForm, apyAtStaking: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ step: "0.1", min: "0" }}
            />
            
            <TextField
              fullWidth
              label="Start Time"
              type="datetime-local"
              value={editForm.startTime}
              onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              fullWidth
              label="End Time"
              type="datetime-local"
              value={editForm.endTime}
              onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.hasCommunityBoost}
                  onChange={(e) => setEditForm({ ...editForm, hasCommunityBoost: e.target.checked })}
                />
              }
              label="Community Boost"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updateStakeMutation.isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            disabled={updateStakeMutation.isLoading || !editForm.amount || !editForm.stakingPeriod || !editForm.apyAtStaking}
          >
            {updateStakeMutation.isLoading ? <CircularProgress size={20} /> : 'Update Stake'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this stake? This action cannot be undone.
          </Typography>
          {selectedStake && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Amount:</strong> {selectedStake.amount.toLocaleString()} MOANI</Typography>
              <Typography variant="body2"><strong>Period:</strong> {formatStakingPeriod(selectedStake.stakingPeriod)}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {selectedStake.isActive ? 'Active' : 'Completed'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteStakeMutation.isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteStakeMutation.isLoading}
          >
            {deleteStakeMutation.isLoading ? <CircularProgress size={20} /> : 'Delete Stake'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unstake Dialog */}
      <Dialog open={unstakeDialogOpen} onClose={() => setUnstakeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UnstakeIcon color="warning" />
          Unstake (Return Principal)
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will return the principal amount to the user's balance but <strong>no rewards will be paid</strong>.
          </Typography>
          {selectedStake && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Principal to Return:</strong> {selectedStake.amount.toLocaleString()} MOANI</Typography>
              <Typography variant="body2"><strong>Period:</strong> {formatStakingPeriod(selectedStake.stakingPeriod)}</Typography>
              <Typography variant="body2"><strong>Rewards Claimed:</strong> {selectedStake.rewardsClaimed.toLocaleString()} MOANI</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Reason (Optional)"
            multiline
            rows={3}
            value={unstakeReason}
            onChange={(e) => setUnstakeReason(e.target.value)}
            placeholder="Enter reason for unstaking..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnstakeDialogOpen(false)} disabled={unstakeMutation.isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUnstakeConfirm} 
            variant="contained" 
            color="warning"
            disabled={unstakeMutation.isLoading}
          >
            {unstakeMutation.isLoading ? <CircularProgress size={20} /> : 'Unstake & Return Principal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserStakes;
